/**
 * privateRoutes.js — the routes that must never be indexed.
 *
 * THIS LIST IS ONE HALF OF A PAIR. The other half is `private_routes` in
 * backend/routes/slice_sitemap_seo.py, which generates the robots.txt
 * Disallow rules. The two solve the same problem at different layers:
 *
 *   robots.txt Disallow  — stops a compliant crawler FETCHING the URL.
 *   <meta name="robots"> — stops a crawler INDEXING the page it fetched.
 *
 * Neither is sufficient alone. A URL that is only Disallow'd can still be
 * indexed (URL-only, no snippet) if something links to it, and a URL that is
 * only noindexed is still crawled on every pass.
 *
 * If the two lists disagree you get exactly the failure the pair exists to
 * prevent: a route that is noindexed but crawlable, or crawlable but not
 * noindexed. frontend/scripts/verify-prerender.js diffs them and fails the
 * build on any mismatch. KEEP THEM IDENTICAL, IN ANY ORDER.
 *
 * Enumerated from src/App.js: every <ProtectedRoute>, plus the seven public
 * routes that are private SURFACE rather than marketing (the auth pair, the
 * account-lifecycle pair, and the two admin panels).
 */
export const PRIVATE_ROUTES = [
  // Auth + account entry points (public routes, private surface)
  "/auth",
  "/signup",
  "/verify",
  "/invite",
  // Admin panels (public routes, admin surface)
  "/td-admin",
  "/td-admin-auth",
  "/master",
  // Application (every ProtectedRoute in App.js)
  "/dashboard",
  "/editor",
  "/inbox",
  "/posts",
  "/notifications",
  "/settings",
  "/automation",
  "/contacts",
  "/ab-testing",
  "/leads",
  "/collabs",
  "/comments",
  "/analytics",
  "/products",
  "/bio",
  "/subscription/success",
  "/connect-instagram",
  "/instagram",
  "/ai-writer",
  "/growth-agent",
  "/debug-instagram",
];

/**
 * Exact match, or a path segment beneath it. NEVER a bare prefix.
 *
 * The top-level namespace is shared with creator bio pages (App.js's
 * trailing `/:slug`), so `pathname.startsWith("/bio")` would noindex
 * /biohacking and `/auth` would take /authentic-fitness with it. Those are
 * customers' pages. RESERVED_SLUGS stops a creator taking `bio` exactly; it
 * does not stop `biohacking`. This is the same anchoring the robots.txt
 * rules use ($ and / forms) — expressed here as code rather than as pattern
 * syntax.
 */
export function isPrivatePath(pathname) {
  if (typeof pathname !== "string" || !pathname) return false;
  // Normalise a trailing slash so "/dashboard/" matches "/dashboard".
  const p = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  return PRIVATE_ROUTES.some((r) => p === r || p.startsWith(`${r}/`));
}
